import { useState, useEffect, useContext, Suspense } from 'react'
import { MenuTitles } from 'constants/menu'
import { ContentLayout, PageLayout } from 'layout'
import {
  AgxRow,
  AgxColumn,
  AgxHeader,
  AgxLabel,
  AgxTextInput,
  AgxBodyText,
  AgxToast,
  AgxDivider,
  AgxFilterableTableHeader,
  Images,
  TableColumn,
  TableHeaderFilter,
} from '@urbanx/agx-ui-components'
import { AgxCheckbox } from '@urbanx/agx.ui-components2'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'

import { AgxToastState } from 'types/commonTypes'
import placeHolderImage from 'assets/images/placeholder-property.png'
import './Listings.scss'
import { ActiveListing, ListingsTableColumn } from 'types/rex'
import {
  GetActiveListingsForAgency,
  SetListingPublishedToPortal,
} from 'services'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useSelectedAgency } from 'hooks'

const ListingsPage = () => {
  const { agencyId } = useParams()
  const queryClient = useQueryClient()
  const [searchText, setSearchText] = useState('')
  const [shouldResetFilters, setShouldResetFilters] = useState(false)
  const { agency: selectedAgency } = useSelectedAgency()

  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })

  const { userAccount, getAuthToken } = useContext(AuthContext)

  const { data: loadedListings, isLoading: isLoadingListings } = useQuery<
    ActiveListing[] | undefined
  >({
    queryKey: ['listings', agencyId],
    queryFn: (queryKey) => GetActiveListingsForAgency(queryKey, getAuthToken),
  })

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['listings', agencyId],
    })
  }, [])

  const [columns, setColumns] = useState<TableColumn[]>([])
  const [listings, setListings] = useState<ActiveListing[]>([])
  const [filteredListings, setFilteredListings] = useState<ActiveListing[]>([])
  const [filters, setFilters] = useState<{
    [id: string]: TableHeaderFilter[]
  }>({})
  const [settingPublishedToPortal, setSettingPublishedToPortal] =
    useState(false)

  const { mutate: setListingPublishedToPortal } = useMutation({
    mutationFn: SetListingPublishedToPortal,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listings', agencyId],
      })
      updateToastState({
        color: 'success',
        message: 'Listing publish status updated',
        open: true,
      })
      setSettingPublishedToPortal(false)
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: ['listings', agencyId],
      })
      updateToastState({
        color: 'error',
        message: 'Error setting listing publish status',
        open: true,
      })
    },
  })

  useEffect(() => {
    const listings = loadedListings?.filter((listing) => {
      if (!searchText) return true
      if (!listing.address?.toLowerCase().includes(searchText.toLowerCase())) {
        return false
      }
      return true
    })

    setListings(listings ?? [])
  }, [loadedListings, searchText])

  useEffect(() => {
    const cols: TableColumn[] = [
      {
        id: ListingsTableColumn.Image,
        name: 'Property',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: ListingsTableColumn.Address,
        name: 'Address',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: ListingsTableColumn.Agents,
        name: 'Agents',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: ListingsTableColumn.Published,
        name: 'Published',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
      {
        id: '',
        name: '',
        options: [],
      },
    ]

    if (!listings) {
      setColumns(cols)
      setShouldResetFilters(true)
      setFilteredListings(listings)
      return
    }

    const agents = listings.flatMap((listing) => listing.agents)

    cols[6].options = Array.from(new Set(agents)).map((agent) => ({
      label: agent,
      value: agent,
      selected: false,
      default: false,
    }))
    cols[9].options = [
      { label: 'Yes', value: 'Yes', selected: false, default: false },
      { label: 'No', value: 'No', selected: false, default: false },
    ]

    setColumns(cols)
    setShouldResetFilters(true)
    setFilteredListings(listings)
  }, [listings])

  const applyFilters = (filter: { id: string; value: TableHeaderFilter[] }) => {
    const updatedFilters = { ...filters, [filter.id]: filter.value }
    setFilters(updatedFilters)

    if (!listings) {
      setFilteredListings([])
      return
    }

    // Check if no filters are selected
    const noFiltersSelected = Object.values(updatedFilters).every((values) =>
      values.every((v) => !v.selected)
    )

    if (noFiltersSelected) {
      setFilteredListings([...listings])
      return
    }

    let filteredRows = [...listings]

    Object.entries(updatedFilters).forEach(([id, values]) => {
      const selected = values.filter((v) => v.selected)
      if (selected.length === 0) return

      if (id === ListingsTableColumn.Agents) {
        filteredRows = filteredRows.filter((row) =>
          row.agents.some((agent) => selected.some((s) => s.value === agent))
        )
      }

      if (id === ListingsTableColumn.Published) {
        filteredRows = filteredRows.filter((row) => {
          const published = row.pushedToPortal

          return (
            (published && selected.some((s) => s.value === 'Yes')) ||
            (!published && selected.some((s) => s.value === 'No'))
          )
        })
      }
    })

    setFilteredListings(filteredRows)
  }

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={selectedAgency?.name || ''}
      currentPageTitle={MenuTitles.WEBSITE_LISTINGS}
    >
      <ContentLayout
        hasSecondaryMenu={true}
        secondaryActiveMenu={MenuTitles.WEBSITE_LISTINGS}
      >
        <AgxToast selector="#agxToast" toastState={toastState} />
        <AgxColumn largeGap>
          <>
            <AgxRow end>
              <AgxTextInput
                id="listingsSearch"
                label=""
                defaultValue={searchText}
                onInputValueChange={({ value }: { value: string }) =>
                  setSearchText(value)
                }
                noHeader
                noOptionalLabel
                placeholder="Search"
                leftIcon={<Images.SearchOutline />}
              />
            </AgxRow>
            <AgxDivider />
            <AgxRow>
              <table className="listingsTable">
                <tbody>
                  <AgxFilterableTableHeader
                    columns={columns}
                    shouldResetFilters={shouldResetFilters}
                    setShouldResetFilters={setShouldResetFilters}
                    onFilterChange={applyFilters}
                  />
                  {isLoadingListings ? (
                    <Suspense>
                      <tr>
                        <td colSpan={11}>
                          <AgxLabel large>Loading Listings...</AgxLabel>
                        </td>
                      </tr>
                    </Suspense>
                  ) : (
                    <>
                      {filteredListings?.map((listing, index) => {
                        return (
                          <tr
                            id={`listing-${listing.listingId}-${index}`}
                            key={`listing-${listing.listingId}-${index}`}
                            className="listingsRow"
                          >
                            <td colSpan={3} align="left">
                              <img
                                className="propertyImage"
                                width={60}
                                src={listing.imageUri ?? placeHolderImage}
                              />
                            </td>
                            <td colSpan={3} align="left">
                              <AgxHeader size={4}>{listing.address}</AgxHeader>
                            </td>
                            <td colSpan={3} align="left">
                              <AgxBodyText small>
                                {listing.agents?.join(', ')}
                              </AgxBodyText>
                            </td>
                            <td colSpan={3} align="left">
                              <AgxCheckbox
                                id={`publishedCheckbox-${listing.listingId}`}
                                checked={listing.pushedToPortal}
                                disabled={settingPublishedToPortal}
                                onChange={(value) => {
                                  if (listing.pushedToPortal === value) return

                                  setSettingPublishedToPortal(true)
                                  setListingPublishedToPortal({
                                    getAuthToken,
                                    ListingId: listing.listingId,
                                    PublishToPortal: value,
                                  })
                                  setFilteredListings((prev) =>
                                    prev.map((prevListing) => {
                                      if (
                                        prevListing.listingId ===
                                        listing.listingId
                                      ) {
                                        return {
                                          ...prevListing,
                                          pushedToPortal: value,
                                        }
                                      }
                                      return prevListing
                                    })
                                  )
                                }}
                              />
                            </td>
                          </tr>
                        )
                      })}
                      {filteredListings.length === 0 && !isLoadingListings && (
                        <tr>
                          <td colSpan={11}>
                            <AgxLabel large>No Listings found</AgxLabel>
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </AgxRow>
          </>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  )
}

export default ListingsPage
